import apiClient from './apiClient';
import { Album } from '@/types/album';
import { Song } from '@/types/song';
import { AlbumCoverArtUrlResponse } from '@/types/api';
import { appDataStore } from '@/services/cache/appDataStore';

interface AlbumListParams {
  search?: string;
  sort?: 'name' | 'created' | 'updated';
  order?: 'asc' | 'desc';
}

export const albumsApi = {
  /**
   * List albums from the cache (populated at startup, refreshed in the
   * background). Returns an empty list when no albums exist or the album
   * endpoints are not yet deployed.
   */
  async listAlbums(params: AlbumListParams = {}): Promise<Album[]> {
    let albums = appDataStore.getAllAlbums();

    if (params.search) {
      const s = params.search.toLowerCase();
      albums = albums.filter((a) => a.albumName.toLowerCase().includes(s));
    }

    const sortField = params.sort || 'updated';
    const order = params.order || 'desc';
    const key =
      sortField === 'name' ? 'albumName' : sortField === 'created' ? 'created' : 'lastUpdated';

    return [...albums].sort((a, b) => {
      const av = (a[key as keyof Album] as string | number | undefined) ?? '';
      const bv = (b[key as keyof Album] as string | number | undefined) ?? '';
      if (order === 'asc') return av > bv ? 1 : -1;
      return av < bv ? 1 : -1;
    });
  },

  /**
   * An album's songs, in track order, as full Song objects from the song cache
   * (so list-row metadata like revision counts render). Optionally filtered by
   * a scoped search. Hidden songs are excluded.
   */
  async getAlbumSongs(albumId: number, search?: string): Promise<Song[]> {
    await appDataStore.ensureAlbumDetails(albumId);
    const tracks = appDataStore.getAlbumSongs(albumId);

    let songs: Song[] = tracks
      .map((t) => {
        const full = appDataStore.getSong(t.songId);
        if (full) return full;
        // Fallback for a track whose song is not in the list cache.
        return {
          songId: t.songId,
          songName: t.songName,
          coverArtUrl: t.coverArtUrl ?? undefined,
        } as Song;
      })
      .filter((s) => !s.hidden);

    if (search) {
      const s = search.toLowerCase();
      songs = songs.filter((song) => song.songName.toLowerCase().includes(s));
    }

    return songs;
  },

  async getCoverArtUrl(
    albumId: number,
    params?: { expiration?: number }
  ): Promise<AlbumCoverArtUrlResponse> {
    const response = await apiClient.get(`/albums/${albumId}/cover-art`, { params });
    return response.data;
  },
};
